import * as ReportActions from "src/app/store/report/actions";
import * as SurveyActions from "src/app/store/survey/actions";
import * as UserActions from "src/app/store/user/actions";
import * as FromReport from "src/app/store/report/selectors";
import * as FromSurvey from "src/app/store/survey/selectors";
import * as FromUser from "src/app/store/user/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { AppRoutes, TOAST_DEFAULT_DURATION } from "src/app/constants";
import { RootState } from "src/app/store";
import { ActionButtonDto, CreateReportDto, ReportDto, SurveyDefinitionDto } from "@api";
import { first, skipWhile, withLatestFrom } from "rxjs/operators";
import { PopoverButton, PopoverComponent } from "src/app/components/popover/popover.component";
import { NavController, Platform, PopoverController, ToastController } from "@ionic/angular";
import { Printer } from "@awesome-cordova-plugins/printer/ngx";

@Component({
  selector: "app-report-detail",
  templateUrl: "./report-detail.page.html",
  styleUrls: ["./report-detail.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportDetailPage implements OnInit {
  @ViewChild("print") private print: ElementRef<HTMLDivElement>;

  public routes: typeof AppRoutes = AppRoutes;
  public slug$: Observable<string> = this.store.select(FromRouter.selectSurveySlug);
  public month$: Observable<string> = this.store.select(FromRouter.selectMonthFromQueryParams);
  public year$: Observable<string> = this.store.select(FromRouter.selectYearFromQueryParams);
  public actionButton$: Observable<ActionButtonDto> = this.store.select(FromUser.selectActionButton);
  public definition$: Observable<SurveyDefinitionDto>;
  public reports$: Observable<ReportDto[]>;
  public date: Date;
  public showActionButton: boolean;
  public breadcrumbs: { name: string; link?: string }[] = [
    { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${this.routes.home}` },
  ];

  constructor(
    private readonly store: Store<RootState>,
    private readonly i18n: TranslateService,
    private readonly navController: NavController,
    private readonly popoverController: PopoverController,
    private readonly toastController: ToastController,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly platform: Platform,
    private readonly printer: Printer,
  ) {}

  public ngOnInit(): void {
    this.month$.pipe(first()).subscribe(month => {
      this.showActionButton = new Date().getMonth() === +month;
    });

    this.slug$.pipe(first()).subscribe(slug => {
      this.definition$ = this.store.select(FromSurvey.selectDefinitionBySlug(slug));
      this.store.dispatch(SurveyActions.getDefinitionBySlug({ slug }));
      this.store.dispatch(ReportActions.getReportResponses({ slug }));
      this.store.dispatch(UserActions.getActionButton());
    });

    this.definition$
      .pipe(
        skipWhile(definition => !definition),
        withLatestFrom(this.month$),
        withLatestFrom(this.year$),
        first(),
      )
      .subscribe(([[definition, month], year]) => {
        this.date = new Date();
        this.date.setMonth(+month - 1);
        this.date.setFullYear(+year);
        const dataSource = this.getDataSource(definition.slug);
        this.reports$ = this.store.select(FromReport.selectReportsForDate(dataSource, +month, +year));
        this.breadcrumbs.push({ name: definition.name, link: `/${AppRoutes.reports}/${definition.slug}/${AppRoutes.history}` });
        this.changeDetector.detectChanges();
      });
  }

  public async presentPopover(event: Event): Promise<void> {
    const buttons: PopoverButton[] = [
      {
        text: this.i18n.instant("REPORT_DETAIL.EDIT"),
        handler: (): void => {
          this.slug$.pipe(withLatestFrom(this.month$), withLatestFrom(this.year$), first()).subscribe(([[slug, month], year]) => {
            this.navController.navigateRoot([slug], { queryParams: { month, year } });
          });
        },
      },
      {
        text: this.i18n.instant("BUTTONS.PRINT"),
        handler: (): Promise<void> => this.printPage(),
      },
    ];
    const popover = await this.popoverController.create({
      component: PopoverComponent,
      componentProps: { buttons },
      event,
    });

    await popover.present();
  }

  private async printPage(): Promise<void> {
    if (this.platform.is("capacitor")) await this.printCapacitor();
    else this.printBrowser();
  }

  private printBrowser(): void {
    const printWindow = window.open("", "PRINT");
    const html = `
      <html>
        <head>
          <title>${Intl.DateTimeFormat("de-DE", { month: "long" }).format(this.date)} ${this.date.getFullYear()}</title>
        </head>
        <body style="display: flex; flex-direction: column; max-width: 1800px; margin: auto; padding: 32px;">${
          this.print.nativeElement.innerHTML
        }</body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = (): void => {
      printWindow.focus();
      printWindow.print();
    };
  }

  private async printCapacitor(): Promise<void> {
    const canPrint = await this.printer.isAvailable();
    if (canPrint) {
      await this.printer.print(this.print.nativeElement.innerHTML, {
        name: Intl.DateTimeFormat("de-DE", { month: "long" }).format(this.date),
        autoFit: true,
      });
    } else {
      const toast = await this.toastController.create({
        color: "danger",
        message: this.i18n.instant("REPORT_DETAIL.PRINTER_NOT_AVAILABLE"),
        duration: TOAST_DEFAULT_DURATION,
      });
      await toast.present();
    }
  }

  private getDataSource(slug: string): CreateReportDto.DataSourceEnum {
    return slug === AppRoutes.successReport ? CreateReportDto.DataSourceEnum.SuccessReport : CreateReportDto.DataSourceEnum.BwaReport;
  }
}
